import type { ApiContext } from "../context";
import { type ApiNotAuthorized, InvalidFormField, UnexpectedApiError, ValidationError } from "../errors";
import { parse } from "../model/common";
import { AdminFees, FeePolicy, type FeePolicyInput } from "../model/fees";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

/** `/fees`: the customer fee rates. Unauthenticated; pass `anonymous`. */
export class FeesApi {
  constructor(private readonly t: Transport) {}

  async current(ctx: ApiContext): Promise<Result<FeePolicy, never>> {
    const r = await this.t.get(ctx, "/fees");
    return forward(
      r,
      (j) => parse(FeePolicy, j),
      (e) => {
        throw new UnexpectedApiError(e);
      },
    );
  }
}

/** Platform staff: `/admin/fees`, the policy every Stripe checkout is priced from. */
export class AdminFeesApi {
  constructor(private readonly t: Transport) {}

  /** The policy plus what it has collected, the two fees kept apart. */
  async get(ctx: ApiContext): Promise<Result<AdminFees, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/fees");
    return forward(
      r,
      (j) => parse(AdminFees, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Replaces the whole policy. Applies to the next checkout opened. */
  async set(ctx: ApiContext, policy: FeePolicyInput): Promise<Result<FeePolicy, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.put(ctx, "/admin/fees", { body: policy });
    return forward(
      r,
      (j) => parse(FeePolicy, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }
}
